import React, { useEffect, useState, useCallback } from "react";
import useStore from "../../../../store/useStore";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt, FaTimesCircle, FaBoxOpen } from "react-icons/fa";
import OrderCard from "./OrderCard";
import CancelModal from "./CancelModal";


const PAGE_LIMIT = 10;

const OrdersSection = () => {
  const {
    orders,
    orders_loading,
    orders_error,
    orders_pagination,
    fetchOrders,
    cancelOrder,
    reorderItems,
  } = useStore();

  const navigate = useNavigate();

  const [cancelTarget, setCancelTarget]     = useState(null);
  const [cancelling, setCancelling]         = useState(false);

  const [currentPage, setCurrentPage]       = useState(1);

  // ── Fetch on mount and on page change ──
  useEffect(() => {
    fetchOrders({ page: currentPage, limit: PAGE_LIMIT });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Background polling (always refreshes current page silently) ──
  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === "visible") {
        fetchOrders({ page: currentPage, limit: PAGE_LIMIT, silent: true });
      }
    };
    const intervalId = setInterval(poll, 10000);
    const handleVisibilityChange = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchOrders, currentPage]);

  const handleCancelConfirm = async (remarks) => {
    setCancelling(true);
    try {
      await cancelOrder(cancelTarget, remarks);
      toast.success("Order cancelled");
      setCancelTarget(null);
    } catch (err) {
      toast.error(err.message || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const handleReorder = async (orderId) => {
    const loadingToast = toast.loading("Checking cart…", { id: `reorder-${orderId}` });
    try {
      const result = await reorderItems(orderId);
      toast.dismiss(loadingToast);

      if (!result || result.total === 0) {
        toast.error("No items found in this order.", { id: `reorder-${orderId}` });
        return;
      }

      if (result.error) {
        toast.error(result.error || "Reorder failed. Please try again.", { id: `reorder-${orderId}` });
        return;
      }

      const { added, skipped, failed, total } = result;

      // All items already in cart
      if (added === 0 && skipped === total) {
        toast(
          `🛒 All ${total} item${total !== 1 ? 's' : ''} from this order are already in your cart.`,
          { id: `reorder-${orderId}`, duration: 4000 }
        );
        navigate("/cart");
        return;
      }

      // All new items added
      if (added > 0 && failed === 0) {
        const skipNote = skipped > 0 ? ` (${skipped} already in cart)` : '';
        toast.success(
          `✅ ${added} item${added !== 1 ? 's' : ''} added to cart!${skipNote}`,
          { id: `reorder-${orderId}`, duration: 4000 }
        );
        navigate("/cart");
        return;
      }

      // Partial add
      if (added > 0 && failed > 0) {
        const skipNote = skipped > 0 ? `, ${skipped} already in cart` : '';
        toast(
          `⚠️ ${added} added, ${failed} unavailable${skipNote}.`,
          { id: `reorder-${orderId}`, duration: 5000 }
        );
        navigate("/cart");
        return;
      }

      // Nothing added, all failed
      toast.error(
        "Could not add items to cart. They may no longer be available.",
        { id: `reorder-${orderId}` }
      );
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.message || "Reorder failed. Please try again.", { id: `reorder-${orderId}` });
    }
  };

  const handlePageChange = useCallback((page) => {
    if (page === currentPage || page < 1) return;
    const total = orders_pagination?.total_pages ?? 1;
    if (page > total) return;
    setCurrentPage(page);
  }, [currentPage, orders_pagination]);

  // Derived pagination values
  const totalPages  = orders_pagination?.total_pages  ?? 1;
  const totalOrders = orders_pagination?.total_orders ?? orders.length;

  // Build page number array with gap markers (null = "…")
  const buildPageList = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    const visible = pages.filter(
      (p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1
    );
    const withGaps = [];
    visible.forEach((p, i) => {
      if (i > 0 && p - visible[i - 1] > 1) withGaps.push(null);
      withGaps.push(p);
    });
    return withGaps;
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="mb-6 flex flex-col gap-3 border-b border-gray-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">My Orders</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {totalOrders > 0
              ? `${totalOrders} order${totalOrders !== 1 ? "s" : ""} · Page ${currentPage} of ${totalPages}`
              : "View your current and past orders"}
          </p>
        </div>
        <button
          onClick={() => fetchOrders({ page: currentPage, limit: PAGE_LIMIT })}
          disabled={orders_loading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 active:scale-95"
        >
          <FaSyncAlt className={`h-3.5 w-3.5 ${orders_loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* ── Skeleton loader ── */}
      {orders_loading && orders.length === 0 && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-gray-100 bg-white">
              <div className="h-1 w-full bg-gray-100" />
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="h-2.5 w-12 rounded bg-gray-100" />
                    <div className="h-4 w-40 rounded bg-gray-200" />
                    <div className="h-2.5 w-28 rounded bg-gray-100" />
                  </div>
                  <div className="h-6 w-24 rounded-full bg-gray-100" />
                </div>
                <div className="mt-4 space-y-2.5">
                  {[1, 2].map((j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-gray-100" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 w-3/4 rounded bg-gray-100" />
                        <div className="h-2.5 w-1/2 rounded bg-gray-100" />
                      </div>
                      <div className="h-4 w-12 rounded bg-gray-100" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Error ── */}
      {orders_error && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3.5 text-sm text-red-700">
          <FaTimesCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
          <span>{orders_error}</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!orders_loading && !orders_error && orders.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-md">
            <FaBoxOpen className="h-8 w-8 text-gray-300" />
          </div>
          <p className="mt-4 text-base font-bold text-gray-800">No orders yet</p>
          <p className="mt-1 text-sm text-gray-500">Your placed orders will appear here once you shop.</p>
        </div>
      )}

      {/* ── Orders grid ── */}
      {orders.length > 0 && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {orders.map((order) => (
            <OrderCard
              key={order.order_id ?? order.id}
              order={order}
              onCancel={(id) => setCancelTarget(id)}
              onReorder={handleReorder}
              onRequestReturnItem={(ord, item) => {
                const ordId = ord.order_id ?? ord.id;
                const itmId = item?.order_item_id ?? item?.id;
                const query = itmId ? `?orderId=${ordId}&itemId=${itmId}` : `?orderId=${ordId}`;
                navigate(`/returns/request${query}`, { state: { order: ord, item } });
              }}
            />
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-xs text-gray-400">
            Showing {orders.length} of {totalOrders} orders &nbsp;·&nbsp; Page {currentPage} of {totalPages}
          </p>

          <div className="flex items-center gap-1.5">
            {/* Prev */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || orders_loading}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95"
              title="Previous page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Page numbers */}
            {buildPageList().map((p, i) =>
              p === null ? (
                <span key={`gap-${i}`} className="px-1 text-sm text-gray-300">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  disabled={orders_loading}
                  className={`flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold shadow-sm transition active:scale-95 disabled:cursor-not-allowed ${
                    p === currentPage
                      ? "bg-[#046a38] text-white"
                      : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || orders_loading}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95"
              title="Next page"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Cancel modal ── */}
      {cancelTarget && (
        <CancelModal
          order={orders.find((o) => String(o.order_id ?? o.id) === String(cancelTarget))}
          loading={cancelling}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelTarget(null)}
        />
      )}


    </div>
  );
};

export default OrdersSection;
